import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'hero';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-800 mb-2">
          <img
            src={movie.image}
            alt={movie.title}
            title={`${movie.title} - 91电影视频免费在线观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-950">
              {movie.rating}
            </span>
          </div>
        </div>
        <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-600 mt-0.5">{movie.subcategory} · {movie.year}</p>
      </Link>
    );
  }

  if (variant === 'hero') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group relative block h-[420px] md:h-[600px] rounded-2xl overflow-hidden cursor-pointer"
      >
        <img
          src={movie.image}
          alt={movie.title}
          title={`${movie.title} - 91电影视频免费在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/40 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {movie.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30">
                {tag}
              </span>
            ))}
          </div>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-2">
            {movie.title}
          </h2>
          <p className="text-sm text-foreground-400 max-w-lg line-clamp-2 mb-4">
            {movie.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-primary-400"></i>
              {movie.rating}
            </span>
            <span>{movie.duration}</span>
            <span>{movie.region}</span>
          </div>
        </div>
      </Link>
    );
  }

  // default card
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-800 mb-3">
        <img
          src={movie.image}
          alt={movie.title}
          title={`${movie.title} - 91电影视频免费在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Play button on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-950">
            <i className="ri-play-fill text-xl ml-0.5"></i>
          </div>
        </div>

        {/* Rating badge */}
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-500 text-background-950">
            {movie.rating}
          </span>
        </div>

        {/* New/Hot badge */}
        {movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-background-50">
              NEW
            </span>
          </div>
        )}
        {movie.isHot && !movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-background-50">
              HOT
            </span>
          </div>
        )}
      </div>

      <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-foreground-600">{movie.subcategory}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-700"></span>
        <span className="text-xs text-foreground-600">{movie.year}</span>
      </div>
    </Link>
  );
}